import ErrorIcon from '@mui/icons-material/Error';
import React, { createContext } from 'react';
import styled from 'styled-components';
import {
	BAD_ERRORS,
	BAD_USER_PERMISSION_ERRORS,
	UNSAVED_CHANGES_ERRORS,
	VALIDATION_ERRORS,
} from '../api/http';
import DefaultDialog from '../components/DefaultDialog';
import { errorToast } from '../components/Toast';
import { CUSTOM_ERROR_REGEX, CUSTOM_ERROR_TAG_REGEX } from '../data/constants';
import { AuthData } from './auth';

const StyledMessage = styled.div`
	&:first-letter {
		text-transform: uppercase;
	}
`;

export const ErrorContext = createContext<ErrorData>(null);

export const useErrorContext = () => React.useContext(ErrorContext);

export interface ErrorData {
	setFieldErrorMessages: (fieldErrorMessages: string[]) => Promise<ErrorData>;
	setExposureErrorMessages: (
		exposureErrorMessages: string[],
	) => Promise<ErrorData>;
	setGeneralErrors: (generalErrorMessages: string[]) => Promise<ErrorData>;
	setCurrentErrorObject: (currentErrorObject: any) => Promise<ErrorData>;
	handleErrorResponse: (response: any, title: string) => void;
	handlePolicyholderErrorResponse: (response: any, title: string) => void;
	setTitle: (title: string) => void;
	title: string;
	fieldErrorMessages: string[];
	exposureErrorMessages: string[];
	generalErrorMessages: string[];
	currentErrorObject: any;
}

interface Props {
	authData: AuthData;
}

export class ErrorProvider extends React.Component<Props, ErrorData> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			title: 'Errors',
			currentErrorObject: null,
			fieldErrorMessages: null,
			exposureErrorMessages: null,
			generalErrorMessages: null,
			setTitle: this.setTitle,
			setFieldErrorMessages: this.setFieldErrorMessages,
			setExposureErrorMessages: this.setExposureErrorMessages,
			setGeneralErrors: this.setGeneralErrors,
			handleErrorResponse: this.handleErrorResponse,
			handlePolicyholderErrorResponse: this.handlePolicyholderErrorResponse,
			setCurrentErrorObject: this.setCurrentErrorObject,
		};
	}

	public render() {
		const {
			title,
			fieldErrorMessages,
			exposureErrorMessages,
			generalErrorMessages,
		} = this.state;
		const hasErrors =
			(!!fieldErrorMessages && !!fieldErrorMessages.length) ||
			(!!exposureErrorMessages && !!exposureErrorMessages.length) ||
			(!!generalErrorMessages && !!generalErrorMessages.length);

		const FieldMessages = () => {
			if (!!fieldErrorMessages && fieldErrorMessages.length) {
				return (
					<>
						<h5 style={{ paddingBottom: '5px' }}>Field errors:</h5>
						{fieldErrorMessages.map((message, index) => (
							<StyledMessage key={index}>{message}</StyledMessage>
						))}
					</>
				);
			}
			return null;
		};
		const ExposureMessages = () => {
			if (!!exposureErrorMessages && exposureErrorMessages.length) {
				return (
					<>
						<h5 style={{ paddingBottom: '5px' }}>Exposure errors:</h5>
						{exposureErrorMessages.map((message, index) => (
							<StyledMessage key={index}>{message}</StyledMessage>
						))}
					</>
				);
			}
			return null;
		};

		const GeneralMessages = () => {
			if (!!generalErrorMessages && generalErrorMessages.length) {
				return (
					<>
						{generalErrorMessages.map((message, index) => (
							<StyledMessage key={index}>{message}</StyledMessage>
						))}
					</>
				);
			}
			return null;
		};

		const header = (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<ErrorIcon style={{ paddingRight: '10px' }} color='error' />
				{title}
			</div>
		);

		return (
			<ErrorContext.Provider value={this.state}>
				<DefaultDialog
					actions={[
						{
							text: 'Close',
							onClick: () => this.clearMessages(),
							variant: 'contained',
							color: 'primary',
						},
					]}
					open={hasErrors}
					title={header}
				>
					<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
						<GeneralMessages />
						<FieldMessages />
						<ExposureMessages />
					</div>
				</DefaultDialog>
				{this.props.children}
			</ErrorContext.Provider>
		);
	}

	private clearMessages = () => {
		this.setState({
			fieldErrorMessages: null,
			exposureErrorMessages: null,
			generalErrorMessages: null,
		});
	};

	// TODO: address using error toast for all HTTP response types
	private handleErrorResponse = async (errorResponse: any, title: string) => {
		const response = errorResponse.response;
		if (!response) {
			if (title) {
				errorToast(title);
			}
			return;
		}
		await this.setCurrentErrorObject(response);
		this.setTitle(title);

		if (BAD_USER_PERMISSION_ERRORS.includes(response.status)) {
			this.setGeneralErrors([
				'This user does not have permission to perform this action.',
			]);
			return;
		}

		if (UNSAVED_CHANGES_ERRORS.includes(response.status)) {
			this.setGeneralErrors([
				'Please verify that you have saved all changes and that all required fields are filled out',
			]);
			return;
		}

		if (
			!BAD_ERRORS.includes(response.status) &&
			!VALIDATION_ERRORS.includes(response.status)
		) {
			if (response?.data?.message) {
				errorToast(response?.data?.message);
			} else {
				errorToast(title);
			}
		}

		if (BAD_ERRORS.includes(response.status)) {
			return this.handleBadErrors(response);
		}

		if (VALIDATION_ERRORS.includes(response.status)) {
			// policyholder errors
			if (response.data.fieldErrors) {
				const fieldErrorMessages = response.data.fieldErrors.map(
					(err: any) => err.message && `${err.message}`,
				);
				this.setFieldErrorMessages(fieldErrorMessages);
			}
			if (response.data.policyFieldErrors) {
				const fieldErrors = response.data.policyFieldErrors;

				// exposure errors
				if (
					fieldErrors &&
					fieldErrors.exposureValidationErrors &&
					fieldErrors.exposureValidationErrors.length
				) {
					const exposureErrors = fieldErrors.exposureValidationErrors
						.map((exposureError: any, index: number) => {
							// create error for each exposure
							const exposureName = exposureError.exposureName
								? [
										<div key={exposureError.exposureName + index}>
											{exposureError.exposureName}:
										</div>,
								  ]
								: [];
							const exposureFieldErrors = exposureError.exposureFieldErrors
								?.hasErrors
								? exposureError.exposureFieldErrors.errors.map((err: any) => (
										<div
											style={{ paddingLeft: '5%' }}
											key={exposureError.exposureName + err.fieldName}
										>
											{err.fieldName}: {err.message}
										</div>
								  ))
								: [];
							const fieldGroupErrors = exposureError.exposureFieldErrors
								?.fieldGroupErrors
								? this.handleFieldGroupErrorResponse(
										exposureError.exposureFieldErrors.fieldGroupErrors,
										5,
								  )
								: [];
							const perilErrors = this.handlePerilErrorResponses(
								exposureError.perilValidationErrors,
							);
							return exposureError.hasErrors
								? exposureName
										.concat(exposureFieldErrors)
										.concat(...fieldGroupErrors)
										.concat(...perilErrors)
										.concat(<br />)
								: [];
						})
						.flat();
					await this.state.setExposureErrorMessages(exposureErrors);
				}

				if (
					fieldErrors &&
					fieldErrors.policyFieldErrors &&
					fieldErrors.policyFieldErrors.hasErrors
				) {
					// policy field errors
					const fieldErrorMessages = fieldErrors.policyFieldErrors
						? fieldErrors.policyFieldErrors.errors.map(
								(err: any) => `${err.fieldName}: ${err.message}`,
						  )
						: [];
					const mapFieldGroupErrors = (err: any) => {
						const subFieldGroupErrors = err.errors;
						const subFieldErrors = subFieldGroupErrors.map(
							(e: any) => `${e.fieldName}: ${e.message}`,
						);
						return [[`${err.fieldName}: ${err.locator}`], subFieldErrors];
					};
					const fieldGroupErrorMessages = fieldErrors.policyFieldErrors
						? fieldErrors.policyFieldErrors.fieldGroupErrors.map(
								mapFieldGroupErrors,
						  )
						: [];

					// combine policy field errors with field group errors
					const combinedMessages = fieldGroupErrorMessages.reduce(
						(acc: any, fieldGroupPair: any) => {
							return acc.concat(fieldGroupPair[0]).concat(fieldGroupPair[1]);
						},
						fieldErrorMessages,
					);
					await this.state.setFieldErrorMessages(combinedMessages);
				}
			}
		}
	};

	private handlePerilErrorResponses = (perilValidationErrors: any[]) => {
		if (!perilValidationErrors || !perilValidationErrors.length) {
			return [];
		}
		// map over peril validation array
		return perilValidationErrors.map((validationError: any, index: number) => {
			const fieldErrors = validationError.fieldErrors;
			const perilName = fieldErrors.name
				? [
						<div style={{ paddingLeft: '10%' }} key={fieldErrors.name + index}>
							{fieldErrors.name} {index + 1}:
						</div>,
				  ]
				: [];
			const perilFieldErrors = fieldErrors.errors;
			const perilErrors =
				perilFieldErrors && perilFieldErrors.length
					? perilFieldErrors.map((fieldError: any) => (
							<div style={{ paddingLeft: '10%' }} key={fieldError.fieldName}>
								{fieldError.fieldName}: {fieldError.message}
							</div>
					  ))
					: [];
			const perilGroupErrors = fieldErrors.fieldGroupErrors;
			const perilGroupFieldErrors = this.handleFieldGroupErrorResponse(
				perilGroupErrors,
				10,
			);
			return perilName
				.concat(perilErrors)
				.concat(...perilGroupFieldErrors)
				.concat(<br />);
		});
	};

	private handleFieldGroupErrorResponse = (
		fieldGroupErrors: any[],
		padding: number,
	) => {
		if (!fieldGroupErrors || !fieldGroupErrors.length) {
			return [];
		}
		return fieldGroupErrors.map((fieldGroup: any, index: number) => {
			const fieldGroupName = fieldGroup.fieldName
				? [
						<div
							style={{ paddingLeft: `${padding}%` }}
							key={fieldGroup.fieldName + index}
						>
							{fieldGroup.fieldName} {index + 1}:
						</div>,
				  ]
				: [];
			const errors =
				fieldGroup.errors && fieldGroup.errors.length
					? fieldGroup.errors.map((err: any) => (
							<div
								style={{ paddingLeft: `${padding + 5}%` }}
								key={fieldGroup.fieldName + err.fieldName}
							>
								{err.fieldName}: ${err.message}
							</div>
					  ))
					: [];
			return fieldGroupName.concat(errors).concat(<br />);
		});
	};

	private handlePolicyholderErrorResponse = async (
		errorResponse: any,
		title: string,
	) => {
		const response = errorResponse.response;
		if (!response) {
			return;
		}
		await this.setCurrentErrorObject(response);
		this.setTitle(title);

		if (
			!BAD_ERRORS.includes(response.status) &&
			!VALIDATION_ERRORS.includes(response.status)
		) {
			errorToast(title);
		}

		if (BAD_ERRORS.includes(response.status)) {
			return this.handleBadErrors(response);
		}

		if (VALIDATION_ERRORS.includes(response.status)) {
			const fieldErrors = response.data.fieldErrors;

			const fieldErrorMessages = fieldErrors
				? fieldErrors.map((err: any) => `${err.debug}`)
				: [];
			await this.state.setFieldErrorMessages(fieldErrorMessages);
		}
	};

	private setTitle = (title: string) => {
		const promise = new Promise<ErrorData>((resolve) => {
			this.setState({ title }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setFieldErrorMessages = (fieldErrorMessages: string[]) => {
		const promise = new Promise<ErrorData>((resolve) => {
			this.setState({ fieldErrorMessages }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setExposureErrorMessages = (exposureErrorMessages: string[]) => {
		const promise = new Promise<ErrorData>((resolve) => {
			this.setState({ exposureErrorMessages }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setGeneralErrors = (generalErrorMessages: string[]) => {
		const promise = new Promise<ErrorData>((resolve) => {
			this.setState({ generalErrorMessages }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setCurrentErrorObject = (currentErrorObject: any) => {
		const promise = new Promise<ErrorData>((resolve) => {
			this.setState({ currentErrorObject }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private handleBadErrors = (response: any) => {
		if (!response || !response.data || !response.data.message) return;

		const responseDataMessage = response.data.message;
		const matchedCustomErrorMessages: string[] =
			responseDataMessage.match(CUSTOM_ERROR_REGEX);
		// custom error tag
		if (matchedCustomErrorMessages && matchedCustomErrorMessages.length) {
			const firstCustomError = matchedCustomErrorMessages.map(
				(errorMessage: string) =>
					errorMessage.replace(CUSTOM_ERROR_TAG_REGEX, ''),
			)[0];
			this.setGeneralErrors([firstCustomError]);
		} else {
			this.setGeneralErrors([responseDataMessage]);
		}
	};
}

export default ErrorProvider;
